import { Component, Input, Output, EventEmitter, signal, forwardRef, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconComponent, type IconName } from '../../../icons/icon.component';

@Component({
  selector: 'app-toggle',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './toggle.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: []
})
export class ToggleComponent implements ControlValueAccessor, OnChanges {
  @Input() checked: boolean = false;
  @Input() disabled: boolean = false;
  @Input() label: string = '';
  @Input() className: string = '';
  @Input() useIcons: boolean = false;
  @Input() onIcon: IconName = 'moon';
  @Input() offIcon: IconName = 'sun';
  @Input() iconSize: number = 12;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() labelPosition: 'before' | 'after' = 'after';
  @Input() auditContext: string = '';


  @Output() toggleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() checkedChange: EventEmitter<boolean> = this.toggleChange;


  /** Internal state; kept in sync with @Input checked and ControlValueAccessor.writeValue. */
  isChecked = signal(false);

  private onChange: (value: boolean) => void = () => { };
  private onTouched: () => void = () => { };



  ngOnChanges(changes: SimpleChanges): void {
    if (changes['checked']) {
      this.isChecked.set(changes['checked'].currentValue);
    }
  }

  handleToggle(): void {
    if (this.disabled) return;

    const newValue = !this.isChecked();
    this.isChecked.set(newValue);
    this.toggleChange.emit(newValue);
    this.onChange(newValue);
    this.onTouched();
  }

  writeValue(value: boolean): void {
    this.isChecked.set(value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
